import { QueryClient } from "@tanstack/react-query";
import { installQueryTrace } from "./perf-trace";

/**
 * Global React Query defaults (system-wide loading/caching policy).
 *
 * Goal: when the user navigates between app pages, cached data must be
 * visible instantly. Background refresh continues, but never blanks the UI.
 *
 * - staleTime 5 min: avoid refetching on every mount within the freshness window.
 * - gcTime 30 min: keep cached entries around long enough for tab-switching.
 * - refetchOnMount: false → mount uses cached data immediately; only refetches
 *   when the entry is stale (then keeps showing cached data while refetching).
 * - refetchOnWindowFocus: false → window focus no longer triggers refetches.
 * - retry: 1 → fail fast on truly broken queries instead of hammering.
 *
 * We do NOT set `placeholderData` globally: a global `(prev) => prev` would
 * leak data across brand/workspace switches when query keys change. Hooks
 * that need keepPreviousData behavior should opt in explicitly.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Subscribe to the React Query cache for perf tracing (no-op unless
// PERF_TRACE is enabled).
installQueryTrace(queryClient);
